import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FEATURES } from '@/shared/config/features'
import { queryKeys } from '@/shared/constants/query-keys'
import type { FlowDefinitionInput, FlowScope } from '../models/flow'
import {
  createFlow,
  deactivateFlow,
  getFlow,
  getFlows,
  updateFlow
} from '../services/flows.service'

/**
 * Lista de definiciones de flujo (filtrable por scope y tipo de cliente).
 *
 * El gate vive ACÁ y no en cada pantalla porque la lista de clientes la llamaba sin condición para
 * derivar sus pestañas: con el portal apagado igual salía el request, y detrás el backend hacía
 * cuatro `$lookup` por cliente para llenar columnas que nadie renderiza (R-04 de la auditoría
 * preproducción). Un gate por call site se olvida; éste no.
 */
export function useFlowsQuery(scope?: FlowScope, clientType?: 'individual' | 'juridical') {
  return useQuery({
    queryKey: queryKeys.flows.list(clientType ? `${scope ?? ''}:${clientType}` : scope),
    queryFn: () => getFlows(scope, clientType),
    enabled: FEATURES.clientPortal
  })
}

/** Detalle de una definición (para el editor). */
export function useFlowQuery(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.flows.detail(id ?? ''),
    queryFn: () => getFlow(id as string),
    enabled: !!id
  })
}

/** Mutaciones CRUD; invalidan la lista al terminar. */
export function useFlowMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: queryKeys.flows.all })

  const create = useMutation({
    mutationFn: (payload: FlowDefinitionInput) => createFlow(payload),
    onSuccess: invalidate
  })

  const update = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<FlowDefinitionInput> }) =>
      updateFlow(id, payload),
    onSuccess: invalidate
  })

  const deactivate = useMutation({
    mutationFn: (id: string) => deactivateFlow(id),
    onSuccess: invalidate
  })

  return { create, update, deactivate }
}
